import { FactorBreakdown } from "@/components/FactorBreakdown";
import { ForecastDaySelector } from "@/components/forecast/ForecastDaySelector";
import { OpportunityCard } from "@/components/forecast/OpportunityCard";
import { LocationSearch } from "@/components/LocationSearch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useAuth } from "@/hooks/useAuth";
import { useLocationForecastRange } from "@/hooks/useLocationForecast";
import { useLocations } from "@/hooks/useLocations";
import { useTodaysPredictions } from "@/hooks/usePredictions";
import { usePreferenceValue } from "@/hooks/usePreferences";
import { useForecastLocation } from "@/hooks/useForecastLocation";
import { useForecastNavigation } from "@/hooks/useForecastNavigation";
import { formatQualityScore, getQualityBadgeClasses, normalizeQualityCategory } from "@/lib/qualityScore";
import { formatTimeInTimezone, formatTimeRange } from "@/lib/timezone";
import { formatWindSpeed, type WindSpeedUnit } from "@/lib/units";
import { Check, ChevronDown, CloudSun, Droplets, Eye, Heart, Loader2, MapPin, Star, Sunrise as SunriseIcon, Sunset as SunsetIcon, Thermometer, Wind } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Link } from "wouter";

export default function Forecast() {
  const { isAuthenticated } = useAuth();
  const windSpeedPreference = usePreferenceValue("windSpeedUnit", "MPH");
  const windSpeedUnit: WindSpeedUnit =
    windSpeedPreference === "KMH"
      ? "kmh"
      : windSpeedPreference === "MS"
        ? "ms"
        : windSpeedPreference === "KNOTS"
          ? "knots"
          : "mph";
  const [region, setRegion] = useState<"usa" | "global">("usa");
  const [typeFilter, setTypeFilter] = useState<"all" | "sunrise" | "sunset">("all");

  // Use extracted hook for location from URL + state
  const { selectedLocation, setSelectedLocation, clearLocation } = useForecastLocation();

  const [showSunriseBreakdown, setShowSunriseBreakdown] = useState(false);
  const [showSunsetBreakdown, setShowSunsetBreakdown] = useState(false);

  // Ref for scrolling to forecast section
  const forecastSectionRef = useRef<HTMLDivElement>(null);

  // Locations hook for saving locations
  const { locations, createLocationAsync, isCreating } = useLocations();

  // Note: Location from URL + state management is now handled by useForecastLocation hook
  // (extracted for testability and to reduce component complexity)

  // Fetch predictions from backend
  const { predictions, isLoading, isEmpty, usedFallback } = useTodaysPredictions({
    region: region === "usa" ? "USA" : "GLOBAL",
    type: typeFilter === "all" ? undefined : typeFilter === "sunrise" ? "SUNRISE" : "SUNSET",
  });

  // Fetch 7-day forecast for selected location
  const { forecastsByDate, dates: forecastDates, isLoading: isLoadingForecast, error: forecastError } = useLocationForecastRange({
    latitude: selectedLocation?.lat ?? 0,
    longitude: selectedLocation?.lon ?? 0,
    days: 7,
    timezone: selectedLocation?.timezone,
    enabled: !!selectedLocation,
  });

  // Navigation hook (extracted)
  const {
    selectedDayIndex,
    setSelectedDayIndex,
    handlePreviousDay,
    handleNextDay,
    canGoPrevious,
    canGoNext,
    formatDate,
  } = useForecastNavigation(forecastDates);

  // Get the currently selected day's forecast
  const locationForecast = useMemo(() => {
    if (!forecastDates.length || selectedDayIndex >= forecastDates.length) return null;
    const selectedDate = forecastDates[selectedDayIndex];
    return forecastsByDate[selectedDate] ?? null;
  }, [forecastsByDate, forecastDates, selectedDayIndex]);

  // Clear location if forecast fetch fails
  useEffect(() => {
    if (forecastError && selectedLocation) {
      // Forecast fetch failed (error suppressed for UI)
      clearLocation();
      toast.error("Failed to load forecast for this location");
    }
  }, [forecastError, selectedLocation, clearLocation]);

  // Transform predictions to UI format
  const opportunities = useMemo(() => {
    return predictions.map((p) => {
      // Use location's timezone for display, fallback to UTC
      const locationTimezone = p.timezone || 'UTC';
      return {
        id: p.id,
        location: { name: p.locationName },
        date: p.date,
        type: p.type?.toLowerCase() as "sunrise" | "sunset",
        quality: {
          category: p.qualityCategory,
          score: p.qualityScore,
        },
        time: formatTimeInTimezone(p.eventTime, locationTimezone),
        goldenHour: {
          start: formatTimeInTimezone(p.goldenHourStart, locationTimezone),
          end: formatTimeInTimezone(p.goldenHourEnd, locationTimezone),
        },
        weather: {
          cloudCoverTotal: p.cloudCoverTotal ?? 0,
          humidity: p.humidity ?? 0,
          visibility: (p.visibility ?? 10) * 1000, // Convert km to m
          wind: p.windSpeed ?? 0,
        },
      };
    });
  }, [predictions]);

  // formatDate now comes from useForecastNavigation hook
  // Quality color and formatting functions imported from @/lib/qualityScore

  // Handle location selection from search
  const handleLocationSelect = useCallback((lat: number, lon: number, name: string, timezone?: string) => {
    // Validation is handled inside the hook
    setSelectedLocation({ lat, lon, name, timezone });

    // Scroll to the forecast section after a short delay to allow state update
    setTimeout(() => {
      forecastSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, [setSelectedLocation]);

  // Save location to user's saved locations
  const handleSaveLocation = useCallback(async () => {
    if (!selectedLocation) return;
    if (!isAuthenticated) {
      toast.error("Please sign in to save locations");
      return;
    }

    try {
      await createLocationAsync({
        name: selectedLocation.name,
        latitude: selectedLocation.lat,
        longitude: selectedLocation.lon,
        timezone: selectedLocation.timezone,
      });
      toast.success(`${selectedLocation.name} saved to your locations!`);
    } catch {
      toast.error(`Failed to save ${selectedLocation.name}`);
    }
  }, [selectedLocation, isAuthenticated, createLocationAsync]);

  // Check if the selected location is already saved
  const isLocationSaved = useMemo(() => {
    if (!selectedLocation || !locations) return false;
    return locations.some(
      loc =>
        Math.abs(loc.latitude - selectedLocation.lat) < 0.01 &&
        Math.abs(loc.longitude - selectedLocation.lon) < 0.01
    );
  }, [selectedLocation, locations]);

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Header */}
      <section
        className="relative py-12 sm:py-16 md:py-24 lg:py-32 bg-cover bg-center"
        style={{ backgroundImage: 'url(/hero-forecast.webp)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black/80" />
        <div className="relative z-10 container text-center text-white">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 animate-fade-in drop-shadow-lg">
            Sunrise & Sunset Forecast
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-white/90 max-w-2xl mx-auto drop-shadow-md">
            Discover the perfect moments for breathtaking photography with AI-powered predictions
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="container py-8 sm:py-10 md:py-12">
        {/* Saved Locations Section - Only show if authenticated with saved locations */}
        {isAuthenticated && locations.length > 0 && (
          <div className="mb-10 sm:mb-12 md:mb-16">
            <div className="text-center mb-6">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Heart className="w-6 h-6 text-rose-500" />
                <h2 className="text-2xl sm:text-3xl font-bold">Your Saved Locations</h2>
              </div>
              <p className="text-muted-foreground">Quick access to forecasts for your favorite spots</p>
            </div>

            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {locations.map((location) => (
                <button
                  type="button"
                  key={location.id}
                  className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm hover:shadow-lg transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  onClick={() =>
                    handleLocationSelect(
                      location.latitude,
                      location.longitude,
                      location.name,
                      location.timezone ?? undefined
                    )
                  }
                >
                  <MapPin className="w-6 h-6 mx-auto mb-2 text-amber-500" />
                  <span className="block font-medium truncate">{location.name}</span>
                  {location.isPrimary && (
                    <span className="mt-1 inline-flex items-center rounded-full bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground">
                      <Star className="w-3 h-3 mr-1" /> Primary
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="text-center mt-4">
              <Link href="/locations">
                <Button variant="link">Manage Locations →</Button>
              </Link>
            </div>
          </div>
        )}

        {/* Top 10 Section */}
        <div className="mb-10 sm:mb-12 md:mb-16">
          <div className="text-center mb-6 sm:mb-8">
            <div className="flex items-center justify-center gap-2 sm:gap-3 mb-4">
              <Star className="w-6 h-6 sm:w-8 sm:h-8 text-amber-500" />
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold">Today's Top 10 Opportunities</h2>
            </div>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              The most spectacular sunrise and sunset opportunities for today
            </p>
          </div>

          {/* Region Tabs */}
          <Tabs value={region} onValueChange={(v) => setRegion(v as typeof region)} className="mb-6">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2">
              <TabsTrigger value="usa">USA</TabsTrigger>
              <TabsTrigger value="global">Global</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Type Filter */}
          <div className="flex justify-center mb-6 sm:mb-8">
            <ToggleGroup type="single" value={typeFilter} onValueChange={(v) => v && setTypeFilter(v as typeof typeFilter)}>
              <ToggleGroupItem value="all" aria-label="Show all" className="text-sm sm:text-base px-3 sm:px-4">
                All
              </ToggleGroupItem>
              <ToggleGroupItem value="sunrise" aria-label="Show sunrise only" className="text-sm sm:text-base px-3 sm:px-4">
                <SunriseIcon className="w-4 h-4 mr-1 sm:mr-2" />
                Sunrise
              </ToggleGroupItem>
              <ToggleGroupItem value="sunset" aria-label="Show sunset only" className="text-sm sm:text-base px-3 sm:px-4">
                <SunsetIcon className="w-4 h-4 mr-1 sm:mr-2" />
                Sunset
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12" role="status" aria-live="polite">
              <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" aria-hidden="true" />
              <p className="text-muted-foreground">Finding today's {region === "usa" ? "USA" : "global"} opportunities...</p>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && isEmpty && (
            <Card className="card-elevated border-none shadow-premium max-w-2xl mx-auto mb-12">
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Star className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2">No Predictions Available</h3>
                <p className="text-muted-foreground">
                  No top opportunities found for today's {region === "usa" ? "USA" : "global"} forecast. Try searching for a specific location below or check back later.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Opportunities Grid */}
          {!isLoading && !isEmpty && (
          <>
          {usedFallback && (
            <p className="text-xs text-muted-foreground text-center mb-4" data-testid="predictions-fallback-note">
              Live weather scores (client mode) — backend Top 10 cache unavailable
            </p>
          )}
          <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mb-8 sm:mb-10 md:mb-12">
            {opportunities.map((opp, index) => (
              <OpportunityCard
                key={opp.id || `${opp.location.name}-${opp.date}-${opp.type}`}
                opp={opp}
                index={index}
                formatDate={formatDate}
              />
            ))}
          </div>
          </>
          )}
        </div>

        {/* Location Search Section */}
        <div className="max-w-3xl mx-auto mb-12">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-background text-muted-foreground font-medium">Search Specific Location</span>
            </div>
          </div>
        </div>

        {/* Location Search */}
        <Card ref={forecastSectionRef} className="card-elevated border-none shadow-premium max-w-3xl mx-auto">
          <CardContent className="p-8">
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-foreground mb-2">7-Day Location Forecast</h2>
                <p className="text-muted-foreground">
                  Search for any city or location to view a full 7-day sunrise and sunset forecast
                </p>
              </div>

              <LocationSearch onLocationSelect={handleLocationSelect} />

              {/* Selected Location Card */}
              {selectedLocation && (
                <Card className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-amber-200 dark:border-amber-800">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg">
                          <MapPin className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h4 className="text-xl font-bold">{selectedLocation.name}</h4>
                          <p className="text-sm text-muted-foreground">
                            {selectedLocation.lat.toFixed(4)}°, {selectedLocation.lon.toFixed(4)}°
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isAuthenticated && (
                          <Button
                            variant={isLocationSaved ? "outline" : "default"}
                            size="sm"
                            onClick={handleSaveLocation}
                            disabled={isCreating || isLocationSaved}
                          >
                            {isCreating ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : isLocationSaved ? (
                              <Check className="h-4 w-4 mr-2" />
                            ) : (
                              <Heart className="h-4 w-4 mr-2" />
                            )}
                            {isLocationSaved ? "Saved" : "Save Location"}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Location Forecast Details */}
                    <div className="mt-6 pt-4 border-t border-amber-200 dark:border-amber-800">
                      {isLoadingForecast ? (
                        <div className="flex items-center justify-center py-4" role="status" aria-live="polite">
                          <Loader2 className="h-6 w-6 animate-spin text-amber-600" aria-hidden="true" />
                          <span className="ml-2 text-muted-foreground">Loading 7-day forecast...</span>
                        </div>
                      ) : locationForecast && !forecastError ? (
                        <div className="space-y-6">
                          {/* 7-Day Navigation - extracted component */}
                          <ForecastDaySelector
                            forecastDates={forecastDates}
                            forecastsByDate={forecastsByDate}
                            selectedDayIndex={selectedDayIndex}
                            onSelectDay={setSelectedDayIndex}
                            onPrevious={handlePreviousDay}
                            onNext={handleNextDay}
                            canGoPrevious={canGoPrevious}
                            canGoNext={canGoNext}
                          />

                          {/* Current Day Label */}
                          <div className="text-center">
                            <p className="text-sm text-muted-foreground">
                              Showing forecast for{' '}
                              <span className="font-semibold text-foreground">
                                {formatDate(forecastDates[selectedDayIndex] || '')}
                              </span>
                            </p>
                          </div>

                          {/* Sunrise & Sunset Predictions */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Sunrise Card */}
                            <div className="p-4 rounded-lg bg-gradient-to-br from-orange-100 to-amber-50 dark:from-orange-950/40 dark:to-amber-950/30 border border-amber-200 dark:border-amber-800">
                              <div className="flex items-center gap-2 mb-3">
                                <SunriseIcon className="h-5 w-5 text-orange-500" />
                                <span className="font-semibold">Sunrise</span>
                              </div>
                              <div className="flex items-baseline gap-2 mb-2">
                                <span className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                                  {formatTimeInTimezone(locationForecast.sunriseTime, locationForecast.timezone)}
                                </span>
                                <Badge className={getQualityBadgeClasses(locationForecast.sunriseCategory)}>
                                  {normalizeQualityCategory(locationForecast.sunriseCategory)} - {formatQualityScore(locationForecast.sunriseQuality)}
                                </Badge>
                              </div>
                              {locationForecast.goldenHourMorningStart && locationForecast.goldenHourMorningEnd && (
                                <p className="text-sm text-muted-foreground">
                                  Golden Hour:{" "}
                                  {formatTimeRange(locationForecast.goldenHourMorningStart, locationForecast.goldenHourMorningEnd, locationForecast.timezone)}
                                </p>
                              )}
                              
                              {/* Factor Breakdown Button */}
                              {locationForecast.sunriseFactorBreakdown && locationForecast.sunriseFactorBreakdown.length > 0 && (
                                <div className="mt-3">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowSunriseBreakdown(!showSunriseBreakdown)}
                                    className="w-full"
                                  >
                                    {showSunriseBreakdown ? (
                                      <>
                                        <ChevronDown className="h-4 w-4 mr-2 rotate-180" />
                                        Hide Details
                                      </>
                                    ) : (
                                      <>
                                        <ChevronDown className="h-4 w-4 mr-2" />
                                        Why This Score?
                                      </>
                                    )}
                                  </Button>
                                  {showSunriseBreakdown && (
                                    <FactorBreakdown
                                      factors={locationForecast.sunriseFactorBreakdown}
                                      totalScore={locationForecast.sunriseQuality}
                                      category={locationForecast.sunriseCategory}
                                    />
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Sunset Card */}
                            <div className="p-4 rounded-lg bg-gradient-to-br from-purple-100 to-pink-50 dark:from-purple-950/40 dark:to-pink-950/30 border border-purple-200 dark:border-purple-800">
                              <div className="flex items-center gap-2 mb-3">
                                <SunsetIcon className="h-5 w-5 text-purple-500" />
                                <span className="font-semibold">Sunset</span>
                              </div>
                              <div className="flex items-baseline gap-2 mb-2">
                                <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                                  {formatTimeInTimezone(locationForecast.sunsetTime, locationForecast.timezone)}
                                </span>
                                <Badge className={getQualityBadgeClasses(locationForecast.sunsetCategory)}>
                                  {normalizeQualityCategory(locationForecast.sunsetCategory)} - {formatQualityScore(locationForecast.sunsetQuality)}
                                </Badge>
                              </div>
                              {locationForecast.goldenHourEveningStart && locationForecast.goldenHourEveningEnd && (
                                <p className="text-sm text-muted-foreground">
                                  Golden Hour:{" "}
                                  {formatTimeRange(locationForecast.goldenHourEveningStart, locationForecast.goldenHourEveningEnd, locationForecast.timezone)}
                                </p>
                              )}
                              
                              {/* Factor Breakdown Button */}
                              {locationForecast.sunsetFactorBreakdown && locationForecast.sunsetFactorBreakdown.length > 0 && (
                                <div className="mt-3">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowSunsetBreakdown(!showSunsetBreakdown)}
                                    className="w-full"
                                  >
                                    {showSunsetBreakdown ? (
                                      <>
                                        <ChevronDown className="h-4 w-4 mr-2 rotate-180" />
                                        Hide Details
                                      </>
                                    ) : (
                                      <>
                                        <ChevronDown className="h-4 w-4 mr-2" />
                                        Why This Score?
                                      </>
                                    )}
                                  </Button>
                                  {showSunsetBreakdown && (
                                    <FactorBreakdown
                                      factors={locationForecast.sunsetFactorBreakdown}
                                      totalScore={locationForecast.sunsetQuality}
                                      category={locationForecast.sunsetCategory}
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Weather Conditions */}
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3 md:gap-4">
                            <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                              <CloudSun className="h-3 w-3 sm:h-4 sm:w-4 text-amber-500 flex-shrink-0" />
                              <span>{locationForecast.cloudCover ?? 0}% clouds</span>
                            </div>
                            <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                              <Droplets className="h-3 w-3 sm:h-4 sm:w-4 text-blue-500 flex-shrink-0" />
                              <span>{locationForecast.humidity ?? 0}% humidity</span>
                            </div>
                            <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                              <Eye className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
                              <span>{(locationForecast.visibility ?? 0).toFixed(1)} km</span>
                            </div>
                            <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                              <Wind className="h-3 w-3 sm:h-4 sm:w-4 text-teal-500 flex-shrink-0" />
                              <span>{formatWindSpeed(locationForecast.windSpeed ?? 0, windSpeedUnit)}</span>
                            </div>
                            <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                              <Thermometer className="h-3 w-3 sm:h-4 sm:w-4 text-red-500 flex-shrink-0" />
                              <span>{(locationForecast.temperature ?? 0).toFixed(1)}°C</span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center">
                          Unable to load forecast for this location. Please try again.
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {!isAuthenticated && (
                <p className="text-sm text-muted-foreground text-center">
                  Sign in to save your favorite locations and get personalized predictions.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
